import { EntityFilterRow } from 'components/eventView/filters/EntityFilterRow';
import { EntityMultifiltersRow } from 'components/eventView/filters/EntityMultifiltersRow';
import { EventEntityDefinition } from 'components/eventView/pages/eventData';
import { EventDescription } from 'components/guide/banner/EventDescription';
import { SplitTitle } from 'components/guide/split/SplitTitle';
import { MainButton } from 'elements/button/MainButton';
import { SearchInput } from 'elements/eventView/atomic/SearchInput';
import { useAnalytics } from 'libs/utils/site/analytics';
import { useContext } from 'react';
import { CtxEventView } from 'session/ctxEventView';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';

interface Props {
    entityDefinition: EventEntityDefinition;
    className?: string;
    id?: string;
}

export const SplitHeader = (props: Props): JSX.Element => {
    const analytics = useAnalytics();
    const ctxEventView = useContext(CtxEventView);
    const entityDefinition = props.entityDefinition;
    const entityState = ctxEventView.entityStates[entityDefinition.id];
    const entity = ctxEventView.eventData?.eventInfo?.otherEntities.find((e) => {
        return e.id === entityDefinition.id;
    });

    function resetFilters() {
        analytics.track('event_ui.filter.clicked', {
            type: 'reset'
        });

        entityState.setSearchTerm('');
        entityState.setFilterIds(null);
    }

    const length = entityState.visible?.length;
    const title = entityDefinition?.name;

    return (
        <S_SplitHeader className={props.className} id={props.id}>
            <S_SplitTitle number={length} title={title} />
            {entityDefinition.description && (
                <S_EventDescription text={entityDefinition.description} />
            )}
            {entity.link && (
                <S_Link
                    key={entity?.link.url}
                    href={entity?.link.url}
                    target="_blank"
                    rel="noreferrer"
                >
                    <S_MainButton type="event" iconSet="far" iconName="arrow-up-right-from-square">
                        {entity?.link.text}
                    </S_MainButton>
                </S_Link>
            )}
            {entityDefinition.searchable && (
                <S_SearchInput
                    value={entityState.searchTerm}
                    onChange={(value) => {
                        entityState.setSearchTerm(value);
                    }}
                />
            )}

            {entityDefinition.categories?.length > 0 && (
                <S_Margin>
                    {entityDefinition.multifilters ? (
                        <EntityMultifiltersRow entityId={entityDefinition.id} />
                    ) : (
                        <EntityFilterRow
                            entityId={entityDefinition.id}
                            isWide={ctxEventView.isWide}
                        />
                    )}
                </S_Margin>
            )}

            {entityState.visible?.length !== entityState.all?.length && (
                <S_Reset
                    type="event"
                    iconSet="far"
                    iconName="times"
                    onClick={() => {
                        resetFilters();
                    }}
                >
                    {Localization.REMOVE_FILTERS} ({entityState.visible?.length}/
                    {entityState.all?.length})
                </S_Reset>
            )}
        </S_SplitHeader>
    );
};

const S_SplitHeader = styled.div`
    display: flex;
    flex-direction: column;
    text-align: center;
    position: relative;
`;

const S_SplitTitle = styled(SplitTitle)`
    margin-bottom: 20px;
`;

const S_EventDescription = styled(EventDescription)`
    text-align: left;
    margin-bottom: 20px;
`;

const S_SearchInput = styled(SearchInput)`
    position: absolute;
    width: 300px;
    right: 0;
    top: -5px;
`;

const S_Reset = styled(MainButton)`
    position: absolute;
    top: 2px;
    left: 0;
`;

const S_Link = styled.a`
    display: flex;
    justify-content: center;
    margin: 0 auto;
    width: fit-content;
`;

const S_MainButton = styled(MainButton)``;

const S_Margin = styled.div`
    margin: 30px 0 0 0;
`;
